import { Empty, Skeleton } from 'antd';
import type { DataNode, EventDataNode } from 'antd/es/tree';

import type { TreeNode } from '@/shared/lib/tree';
import { Button } from '@/shared/ui/Button';
import { EllipsisText } from '@/shared/ui/EllipsisText';
import { Icon } from '@/shared/ui/Icon';
import { IconButton } from '@/shared/ui/IconButton';
import { Tree } from '@/shared/ui/Tree';

import styles from './CategoryTree.module.css';

type CategoryTreeProps = {
  headerTitle?: string;
  isLoading?: boolean;
  showCount?: boolean;
  treeData: TreeNode[];
  loadingKeys: React.Key[];
  expandedKeys: React.Key[];
  selectedKeys: React.Key[];
  onCollapseAll: () => void;
  onExpandAll?: () => void;
  onSelect: (
    selectedKeys: React.Key[],
    info: {
      event: 'select';
      selected: boolean;
      node: EventDataNode<DataNode>;
      selectedNodes: DataNode[];
      nativeEvent: MouseEvent;
    }
  ) => void;
  onExpand: (newExpandedKeys: React.Key[], info: { expanded: boolean; node: EventDataNode<DataNode> }) => void;
  onAddClick?: () => void;
  addButtonText?: string;
  showAddButton?: boolean;
  searchSlot?: React.ReactNode;
  searchQuery?: string;
};

const CategoryTreeSkeleton = () => {
  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <Skeleton.Input active size="small" style={{ width: 120, height: 24 }} />
      </div>
      <div className={styles.treeWrapper}>
        <div className={styles.treeContainer}>
          <div className={styles.skeletonTree}>
            {Array.from({ length: 16 }).map((_, index) => {
              const depth = index % 4;
              const width = 120 + depth * 20;

              return (
                <div key={index} className={styles.skeletonNode}>
                  <Skeleton.Input
                    active
                    size="small"
                    style={{
                      width: `${width}px`,
                      height: 22,
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export const CategoryTree = ({
  headerTitle = '카테고리',
  isLoading = true,
  showCount = true,
  treeData = [],
  loadingKeys,
  expandedKeys,
  selectedKeys,
  onSelect,
  onExpand,
  onCollapseAll,
  onExpandAll,
  onAddClick,
  addButtonText = '추가',
  showAddButton = true,
  searchSlot,
  searchQuery,
  ...props
}: CategoryTreeProps) => {
  if (isLoading) return <CategoryTreeSkeleton />;

  const hasExpandedNodes = expandedKeys.length > 0;
  const isEmpty = treeData.length === 0;
  const emptyDescription = searchQuery ? `"${searchQuery}"에 대한 검색 결과가 없습니다.` : '검색 결과가 없습니다';

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h5 className={styles.title}>{headerTitle}</h5>
          {hasExpandedNodes && onCollapseAll && (
            <IconButton onClick={onCollapseAll} className={styles.iconButton} tooltip="모두 닫기">
              <Icon name="arrow-short-down" size={16} />
            </IconButton>
          )}
          {!hasExpandedNodes && onExpandAll && (
            <IconButton onClick={onExpandAll} className={styles.iconButton} tooltip="모두 열기">
              <Icon name="arrow-short-up" size={16} />
            </IconButton>
          )}
        </div>
        {onAddClick && showAddButton && (
          <div className={styles.headerAdd}>
            <Button className={styles.addButton} prefix={<Icon name="add" size={16} />} onClick={onAddClick}>
              <EllipsisText text={addButtonText} tooltipPlacement="topLeft" />
            </Button>
          </div>
        )}
      </div>
      <section className={styles.treeWrapper}>
        {searchSlot}
        <div className={styles.treeContainer}>
          {isEmpty ? (
            <div className={styles.emptyState}>
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyDescription} />
            </div>
          ) : (
            <Tree
              treeData={treeData as TreeNode[]}
              showCount={showCount}
              expandedKeys={expandedKeys}
              selectedKeys={selectedKeys}
              loadingKeys={loadingKeys}
              onSelect={onSelect}
              onExpand={onExpand}
              {...props}
            />
          )}
        </div>
      </section>
    </div>
  );
};
